import {showCascadeHeldTasksModal} from '../components/modals/CascadeHeldTasksModal';
import {confirmCascadeHeldTasks} from '../../../components/scheduling/scheduling_mutations';
import {dispatch, EVENT_ID} from '../../../containers/event_manager';

/**
 * The most times in a row the confirm prompt may re-raise itself.
 *
 * Each round releases at least one finished task and a chain is finite, so this
 * terminates on its own - but nothing in the code makes that a guarantee, and an
 * endless prompt is a bad way to discover otherwise. Five is well past any real
 * chain; reaching it means the server is reporting tasks it is not moving, and
 * stopping there still leaves a consistent chain.
 */
const MAX_CONFIRM_ROUNDS = 5;

/**
 * Raise the prompt, and raise it again if releasing these tasks walked the chain
 * into more completed work.
 *
 * Confirming does not always finish the job: a chain can hold a second finished
 * task deeper down, which only becomes reachable once the first one moves. Asking
 * once and stopping would leave the chain half-cascaded with nothing to say so.
 */
const promptFor = (heldTaskIds, round = 1) => {
	showCascadeHeldTasksModal({
		heldTaskIds,
		// The confirm goes out as a plain fetch rather than a Relay mutation, so
		// nothing in the store moves on its own, and the socket push that the
		// cascade raises deliberately skips the client that asked for it. Without
		// this the user answers "move them" and watches the completed tasks keep
		// their old dates until a reload.
		onConfirm: ids =>
			confirmCascadeHeldTasks({ids}, response => {
				dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
				const stillHeld = response?.confirmCascadeHeldTasks?.stillHeldTasks;
				if (stillHeld && stillHeld.length > 0 && round < MAX_CONFIRM_ROUNDS) {
					promptFor(stillHeld.map(task => task.id).filter(Boolean), round + 1);
				}
			}),
	});
};

/**
 * Reload the canvas when a cascade moved tasks it is showing.
 *
 * Two different consumers need two different things after a cascade, which is why
 * this is not the only refresh in play:
 *
 * - Relay-backed views (task modal, task lists) get the new dates from
 *   `cascadeMovedTasks` on the mutation payload - selecting the date fields is
 *   enough for Relay to write them into the store by id.
 * - The scheduling canvases keep their own state in a DataManager rather than in
 *   the Relay store, so nothing reaches them that way. They subscribe to
 *   SCHEDULING_RELOAD_DATA, and this is what raises it.
 *
 * Nothing else listens to that event, so on every other page this is a no-op -
 * which is correct, because those pages were already served by the store update.
 */
const reloadIfCascadeMoved = movedTasks => {
	if (movedTasks && movedTasks.length > 0) {
		dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
	}
};

/**
 * Refresh after an ordinary task update whose cascade moved successors.
 *
 * Safe to call on any update response: almost every one carries nothing here.
 *
 * @param response the updateTask mutation payload
 */
export const refreshAfterCascade = response => {
	reloadIfCascadeMoved(response?.updateTask?.cascadeMovedTasks);
};

/**
 * Same, for a Timeline chain drag.
 *
 * The drag writes through a plain fetch rather than a Relay mutation, so the store
 * is not updated for it at all and this reload is the only refresh it gets.
 *
 * @param response the shiftDependencyChain mutation payload
 */
export const refreshAfterCascadeOnChainShift = response => {
	reloadIfCascadeMoved(response?.shiftDependencyChain?.cascadeMovedTasks);
};

/**
 * Offer to move completed tasks that a dependency cascade declined to move.
 *
 * A date change pushes everything downstream that can move, but stops at finished
 * work rather than rewriting it silently, and reports what it left alone. This
 * turns that report into a prompt.
 *
 * Safe to call after any task update: almost every response carries nothing here,
 * in which case this does nothing at all. The user's edit has already committed
 * either way, so declining is a complete and consistent outcome rather than a
 * half-applied one.
 *
 * @param response the updateTask mutation payload
 */
export const promptForCascadeHeldTasks = response => {
	const heldTasks = response?.updateTask?.cascadeHeldTasks;
	if (!heldTasks || heldTasks.length === 0) {
		return;
	}

	const heldTaskIds = heldTasks.map(task => task.id).filter(Boolean);
	if (heldTaskIds.length === 0) {
		return;
	}

	promptFor(heldTaskIds);
};

/**
 * Same prompt, for a Timeline chain drag.
 *
 * The drag writes through a different mutation, so it carries its held tasks on
 * its own payload rather than updateTask's.
 */
export const promptForCascadeHeldTasksAfterChainShift = response => {
	refreshAfterCascadeOnChainShift(response);

	const heldTasks = response?.shiftDependencyChain?.cascadeHeldTasks;
	if (!heldTasks || heldTasks.length === 0) {
		return;
	}
	const heldTaskIds = heldTasks.map(task => task.id).filter(Boolean);
	if (heldTaskIds.length > 0) {
		promptFor(heldTaskIds);
	}
};
